import React, { useContext, useState } from 'react';
import { Grid, SelectChangeEvent } from '@mui/material';
import { SendOutlined } from '@mui/icons-material';

import { useAppSelector } from 'hooks/index';
import { selectUserData } from 'store/user/userSelector';

import { useNotificationContext } from 'app/contexts/NotificationContext/index';
import UserContext from '../../context/UsersContext';
import { UsersViewTitles } from '../../constant/UsersViewTitles.enum';
import SendInvitationListActions from './SendInvitationListActions';
import { Invitation } from '@/operator/graphql/types/Invitation';
import UploadedInvitationList from './UploadedInvitationList';
import SmallHeaderWithIconAndDescription from '@/operator/components/general/header/SmallHeaderWithIconAndDescription';
import {
    FirestoreUserRole,
    InvitedUser,
    useSendEmailInviteMutation,
} from 'generated/graphql';
import { selectPartnerData, selectTeamsForTeamSelector } from 'store/partner/partnerSelector';
import { DropdownOption } from "Interfaces/Dropdown/SelectDropdownInterfaces";
import Dropdown from "Components/Dropdown";

export interface UploadedInvitationListProps {
    emailsAmount: number;
    invitationList: Invitation[];
    setInvitationList: React.Dispatch<React.SetStateAction<Invitation[]>>;
}

const SendInvitationList: React.FC<UploadedInvitationListProps> = ({
    emailsAmount,
    invitationList,
    setInvitationList,
}) => {
    const { uid: userId } = useAppSelector(selectUserData);
    const { instanceId: organizationId } = useAppSelector(selectPartnerData);
    const { showNotification } = useNotificationContext();
    const { setHeader, addInvitation } = useContext(UserContext);

    const [sendUserInvitations] = useSendEmailInviteMutation();

    const teams = useAppSelector(selectTeamsForTeamSelector);
    const teamOptions: DropdownOption[] = teams.map(({ name: teamLabel, key: teamValue }) => ({
        label: teamLabel,
        value: teamValue,
    }));

    const [teamId, setTeamId] = useState(teamOptions[0]?.value|| '');

    const handleCancelInvites = () => {
        setInvitationList([]);
    };

    const handleSendInvites = async () => {
        try {
            const createInviteInput: InvitedUser[] = invitationList.map(
                (invitation) => {
                    return {
                        email: invitation.email,
                        role: invitation.isAdmin
                            ? FirestoreUserRole.OrganizationAdmin
                            : FirestoreUserRole.User,
                    };
                },
            );

            const {
                data: { sendEmailInvite: invitesSent },
            } = await sendUserInvitations({
                variables: {
                    input: {
                        instanceId: organizationId,
                        inviterId: userId,
                        invitations: createInviteInput,
                        teamId
                    },
                },
            });
            setHeader({
                title: UsersViewTitles.InviteUsers,
                Icon: SendOutlined,
            });
            addInvitation(invitesSent);
            showNotification({
                message: `We've sent ${emailsAmount} invitation`,
                severity: 'success',
            });
        } catch (error) {
            console.error(
                'something went wrong sending invitation list',
                error,
                error,
            );
            showNotification({
                message: 'Unable to send email invitation',
                severity: 'error',
            });
        }
    };

    const handleTeamChange = (e: SelectChangeEvent) => {
        const teamId = e.target.value;
        setTeamId(teamId)
    };

    return (
        <Grid container gap="10px">
            <SmallHeaderWithIconAndDescription
                title={`You uploaded ${emailsAmount} emails via CSV`}
                description="Each of these will be used to invite members to your organization. You
        also have the option of making each member an admin."
            />
            <Dropdown
                value={teamId}
                options={teamOptions}
                helperText="Which team will they be invited to?"
                sx={{ marginTop: 2 }}
                fullWidth
                onChange={handleTeamChange}
            />
            <UploadedInvitationList
                invitationList={invitationList}
                setInvitationList={setInvitationList}
            />
            <SendInvitationListActions
                onClickCancelButton={handleCancelInvites}
                onClickSendAndInviteButton={handleSendInvites}
                emailsAmount={invitationList.length}
            />
        </Grid>
    );
};

export default SendInvitationList;
